import React, { useEffect, useState } from 'react';
import { activityLogs } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Search, Filter, Download, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const ActivityLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('All');
  const [filterAction, setFilterAction] = useState('All');
  const [filterUser, setFilterUser] = useState('All');

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, searchTerm, filterModule, filterAction, filterUser]);

  const fetchLogs = async () => {
    try {
      const response = await activityLogs.getAll();
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      alert('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOldLogs = async () => {
    if (user?.role !== 'Admin') {
      alert('Only admins can delete old activity logs');
      return;
    }

    if (window.confirm('Are you sure you want to delete all activity logs older than 1 month? This action cannot be undone.')) {
      try {
        const response = await activityLogs.deleteOld();
        alert(response.data.message || 'Old activity logs deleted successfully!');
        fetchLogs(); // Refresh the logs
      } catch (error) {
        console.error('Failed to delete old logs:', error);
        alert(error.response?.data?.detail || 'Failed to delete old activity logs');
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.item_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Module filter
    if (filterModule !== 'All') {
      filtered = filtered.filter(log => log.module === filterModule);
    }

    // Action filter
    if (filterAction !== 'All') {
      filtered = filtered.filter(log => log.action === filterAction);
    }

    // User filter
    if (filterUser !== 'All') {
      filtered = filtered.filter(log => log.user_email === filterUser);
    }

    setFilteredLogs(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Module', 'Item ID', 'Action', 'User', 'Email', 'Changes'];
    const rows = filteredLogs.map(log => [
      new Date(log.timestamp).toLocaleString(),
      log.module,
      log.item_id,
      log.action,
      log.user_name,
      log.user_email,
      log.changes ? JSON.stringify(log.changes) : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const uniqueModules = ['All', ...new Set(logs.map(log => log.module))];
  const uniqueActions = ['All', ...new Set(logs.map(log => log.action))];
  const uniqueUsers = ['All', ...new Set(logs.map(log => log.user_email))];

  const getActionColor = (action) => {
    switch (action) {
      case 'updated': return 'bg-blue-100 text-blue-700';
      case 'deleted': return 'bg-red-100 text-red-700';
      case 'note_added': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getModuleColor = (module) => {
    switch (module) {
      case 'Inventory': return 'bg-amber-100 text-amber-700';
      case 'Production': return 'bg-orange-100 text-orange-700';
      case 'Orders': return 'bg-blue-100 text-blue-700';
      case 'Expenses': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Activity Logs</h1>
          <p className="text-gray-600 mt-1">Monitor all user actions across modules</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {user?.role === 'Admin' && (
            <button
              onClick={handleDeleteOldLogs}
              className="flex items-center space-x-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition shadow-lg"
            >
              <Trash2 className="w-5 h-5" />
              <span>Delete Old Logs (>30 days)</span>
            </button>
          )}
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition shadow-lg"
          >
            <Download className="w-5 h-5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Total Actions</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{logs.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Updates</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {logs.filter(log => log.action === 'updated').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Deletions</p>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {logs.filter(log => log.action === 'deleted').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Notes Added</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {logs.filter(log => log.action === 'note_added').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by item, user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Module Filter */}
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {uniqueModules.map(module => (
              <option key={module} value={module}>{module}</option>
            ))}
          </select>

          {/* Action Filter */}
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {uniqueActions.map(action => (
              <option key={action} value={action}>
                {action === 'All' ? 'All Actions' : action.replace('_', ' ').toUpperCase()}
              </option>
            ))}
          </select>

          {/* User Filter */}
          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {uniqueUsers.map(user => (
              <option key={user} value={user}>
                {user === 'All' ? 'All Users' : user}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>Showing {filteredLogs.length} of {logs.length} logs</span>
          {(searchTerm || filterModule !== 'All' || filterAction !== 'All' || filterUser !== 'All') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterModule('All');
                setFilterAction('All');
                setFilterUser('All');
              }}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Timestamp</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Module</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Item ID</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Action</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">User</th>
                <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Changes</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 px-6 text-center text-gray-500">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log, index) => (
                  <tr key={`${log.timestamp}-${log.item_id}-${index}`} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getModuleColor(log.module)}`}>
                        {log.module}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="font-semibold text-gray-800">{log.item_id}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                        {log.action.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{log.user_name}</p>
                        <p className="text-xs text-gray-500">{log.user_email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {log.changes ? (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-purple-600 hover:text-purple-700">View Changes</summary>
                          <pre className="mt-2 bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                            {JSON.stringify(log.changes, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
